import type { Capabilities } from '@rtkwlf/rendall';
import dayjs from 'dayjs';
import { Navigate, Outlet } from 'react-router-dom';

import type { FeatureConfig } from '@platform/client/features/feature-config';

import { LegacyCapabilitiesGuard } from '@packages/components/capabilities/legacy-capabilities-guard';
import { SubscriptionsGuard } from '@packages/components/subscriptions/subscriptions-guard/subscriptions-guard';
import { hasAnyRequiredProperty } from '@packages/utils/has-any-required-property';

import { DefaultDateRangeGuard } from './components/default-date-range-guard/default-date-range-guard';
import { HandleLegacyFiltersGuard } from './components/handle-legacy-filters-guard/handle-legacy-filters-guard';
import i18n from './locales/i18n';

const rawLogLegacyCapabilities: Partial<Capabilities> = {
  canAccessRawData: true,
};

const flowDataLegacyCapabilities: Partial<Capabilities> = {
  canAccessDataFlow: true,
};

// TODO: Refactor code to centralize authorization logic also found in useDataExplorerConfiguration
// useDataExplorerConfiguration - packages/hooks/src/use-data-explorer-configuration/use-data-explorer-configuration.tsx
const dataExplorerLegacyCapabilities: Partial<Capabilities> = {
  canAccessAnalyzedData: true,
  canAccessAnalyzedDataLite: true,
};

export default {
  routes: [
    {
      path: 'data-explorer',
      children: [
        {
          path: 'raw-log',
          element: (
            <LegacyCapabilitiesGuard capabilities={rawLogLegacyCapabilities}>
              <Outlet />
            </LegacyCapabilitiesGuard>
          ),
          children: [
            {
              path: 'history',
              lazy: () =>
                import(
                  './raw-log/pages/search-history/raw-log-search-history.page'
                ),
            },
            {
              path: 'sources',
              lazy: () =>
                import(
                  './raw-log/pages/search-sources/raw-log-search-sources.page'
                ),
            },
            {
              path: 'search/:searchId?',
              lazy: () => import('./raw-log/pages/search/raw-log-search.page'),
            },
            {
              path: 'download/:queryId/:outputId/:fileId',
              lazy: () => import('./raw-log/pages/download/download.page'),
            },
          ],
        },
        {
          path: 'flow',
          element: (
            <LegacyCapabilitiesGuard capabilities={flowDataLegacyCapabilities}>
              <Outlet />
            </LegacyCapabilitiesGuard>
          ),
          children: [
            { index: true, element: <Navigate to="./device" replace /> },
            {
              path: ':flowDataType?',
              lazy: () => import('./flow-data/pages/flow-data/flow-data.page'),
            },
          ],
        },
        {
          path: 'event-log/search',
          element: (
            <SubscriptionsGuard
              subscriptions={{
                dataExploration: true,
                dataExplorationLite: true,
              }}
            >
              <LegacyCapabilitiesGuard
                capabilities={dataExplorerLegacyCapabilities}
              />
            </SubscriptionsGuard>
          ),
          children: [
            {
              element: <DefaultDateRangeGuard />,
              children: [
                {
                  element: <HandleLegacyFiltersGuard />,
                  children: [
                    {
                      index: true,
                      async lazy() {
                        const { FilterComponent } = await import(
                          './event-log/pages/event-log-search-v2/event-log-search-v2.page'
                        );
                        return { Component: FilterComponent };
                      },
                    },
                  ],
                },
                {
                  path: ':id',
                  async lazy() {
                    const { IdComponent } = await import(
                      './event-log/pages/event-log-search-v2/event-log-search-v2.page'
                    );
                    return { Component: IdComponent };
                  },
                },
              ],
            },
            {
              path: 'queries',
              lazy: () => import('./pages/saved-queries/saved-queries.page'),
            },
          ],
        },
      ],
    },
  ],

  navigation: ({ legacyCapabilities, subscriptions }) => {
    const hasDataExplorerSubscription =
      subscriptions &&
      (subscriptions?.dataExploration || subscriptions?.dataExplorationLite);

    const hasDataExplorerAccess =
      hasDataExplorerSubscription &&
      legacyCapabilities &&
      hasAnyRequiredProperty(
        dataExplorerLegacyCapabilities,
        legacyCapabilities,
      );

    const hasRawLogAccess =
      legacyCapabilities &&
      hasAnyRequiredProperty(rawLogLegacyCapabilities, legacyCapabilities);

    const hasFlowDataAccess =
      legacyCapabilities &&
      hasAnyRequiredProperty(flowDataLegacyCapabilities, legacyCapabilities);

    return {
      dataExploration: {
        dataExplorer: hasDataExplorerAccess
          ? {
              title: legacyCapabilities.canAccessAnalyzedData
                ? i18n.t('eventLogSearch:eventLogSearch')
                : i18n.t('eventLogSearch:dataExplorerLite'),
              segment: '/data-explorer/event-log/search',
            }
          : null,
        rawLogSearch: hasRawLogAccess
          ? {
              title: i18n.t('logSearch:navItem'),
              segment: '/data-explorer/raw-log/search',
            }
          : null,
        rawLogSearchHistory: hasRawLogAccess
          ? {
              title: i18n.t('logSearchHistory:navItem'),
              segment: '/data-explorer/raw-log/history',
            }
          : null,
        rawLogSources: hasRawLogAccess
          ? {
              title: i18n.t('logSearchSources:navItem'),
              segment: '/data-explorer/raw-log/sources',
            }
          : null,
        flowData: hasFlowDataAccess
          ? {
              title: i18n.t('flow:title'),
              segment: '/data-explorer/flow',
            }
          : null,
      },
    };
  },

  whatsNew: (context) => {
    return [
      {
        newsItemId: 'dataExplorerUIUpdate',
        date: dayjs('2025-05-15T00:00:00Z'),
        hasAccess: !!(
          context.subscriptions?.dataExploration ||
          context.subscriptions?.dataExplorationLite
        ),
      },
    ];
  },
} satisfies FeatureConfig;
